import type { ReactNode } from 'react';
import { summarizePlan, type PlanOperation } from './planValues.js';

/**
 * The values a plan will send to Keap, laid out for reading before approval.
 *
 * Everything shown comes from `summarizePlan` over the server's proposed operations — the
 * same plan the confirmation is bound to — so what a person approves here is what is
 * sent. Contact Type (Keap's own field) and CRM Tags (labels) stay separate sections.
 */
export function PlanSummary({ operations, tagName, typeLabel }: {
  operations: readonly PlanOperation[];
  /** A tag key as the name a person reads. */
  tagName: (key: string) => string;
  /** A Contact Type identifier as the account names it. */
  typeLabel: (id: string) => string;
}) {
  const s = summarizePlan(operations);
  if (s.action === 'none') return null;
  const adding = s.action === 'update';

  return (
    <div className="space-y-4" data-plan-summary="" data-plan-action={s.action}>
      <div>
        <h4 className={HEADING}>Keap action</h4>
        <p className="mt-1 text-sm font-medium text-slate-900" data-plan-title="">{s.title}</p>
        {adding ? (
          <p className="mt-1 text-xs text-slate-600">
            Only the information below is added. Everything already in Keap stays as it is.
          </p>
        ) : null}
      </div>

      {s.fullName || s.jobTitle || s.company ? (
        <Section title={adding ? 'Contact details to add' : 'Contact'} data="contact">
          {s.fullName ? <p className="font-medium text-slate-900" data-plan-name="">{s.fullName}</p> : null}
          {s.jobTitle ? <p data-plan-job-title="">{s.jobTitle}</p> : null}
          {s.company ? <p data-plan-company="">{s.company}</p> : null}
        </Section>
      ) : null}

      {s.emails.length > 0 ? (
        <Section title={s.emails.length > 1 ? 'Emails' : 'Email'} data="email">
          {s.emails.map((e) => <p key={e} data-plan-email="">{e}</p>)}
        </Section>
      ) : null}

      {s.phones.length > 0 ? (
        <Section title={s.phones.length > 1 ? 'Phones' : 'Phone'} data="phone">
          <ul className="space-y-1">
            {s.phones.map((p) => (
              <li key={p.e164} data-plan-phone={p.e164}>
                <span className="text-slate-900">{p.display}</span>
                <span className="block text-xs text-slate-600">
                  Type: {p.typeLabel}{p.primary ? ' · Primary' : ''}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {s.addressLines.length > 0 ? (
        <Section title="Address" data="address">
          <address className="not-italic">
            {s.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
          </address>
        </Section>
      ) : null}

      {s.website ? <Section title="Website" data="website"><p>{s.website}</p></Section> : null}

      {s.contactType ? (
        <Section title="Contact Type" data="contact-type"><p>{typeLabel(s.contactType)}</p></Section>
      ) : null}

      {s.tagKeys.length > 0 ? (
        <Section title="CRM Tags" data="tags">
          <ul className="flex flex-wrap gap-1.5">
            {s.tagKeys.map((k) => (
              <li key={k} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-800">{tagName(k)}</li>
            ))}
          </ul>
          {s.tagsSeparately ? (
            <p className="mt-1 text-xs text-slate-600">Applied to the new contact once Keap has created it.</p>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}

const HEADING = 'text-xs font-semibold uppercase tracking-wide text-slate-500';

function Section({ title, data, children }: { title: string; data: string; children: ReactNode }) {
  return (
    <section data-plan-section={data} className="min-w-0 text-sm text-slate-800 [overflow-wrap:anywhere]">
      <h4 className={HEADING}>{title}</h4>
      <div className="mt-1">{children}</div>
    </section>
  );
}
